import { useMemo, type ReactNode } from 'react';
import { EnquiryAudienceEnum, EnquiryFormPageType } from 'generated/graphql';

import type { LoadedPageCtas } from '~/modules/enquiry/domain/page-ctas';
import {
    PageCtasProvider,
    type PageBookIntentBase,
    type PageCtasProviderProps,
} from '~/modules/enquiry/component/PageCtasProvider';
import { CtaAutoOpen } from '~/modules/enquiry/component/CtaAutoOpen';
import type {
    EnquiryModalContext,
    EnquiryModalProfessional,
} from '~/modules/enquiry/hooks/use-enquiry-modal';
import type { RevampProfessional } from '../api/get-article-revamp-data.server';

/** What the article's primary enquiry button reads while the flat settings decide. */
export const ARTICLE_ENQUIRE_LABEL = 'Enquire Now';

interface ArticlePageCtasProviderProps {
    /** The loader's `pageCtas`; null (Forms off, the lookup failed, the page builder) keeps the flat settings. */
    loaded: LoadedPageCtas | null;
    /** The professional's flat `EnquiryAudience`; Anybody when the page does not know it. */
    enquiryAudience?: EnquiryAudienceEnum | null;
    professional: RevampProfessional;
    enquiryProfessional: EnquiryModalProfessional;
    articleID: number;
    articleTitle: string;
    /** The opening picture, for the enquiry modal's header and the booking gate's pane. */
    heroImage?: string;
    children: ReactNode;
}

/**
 * Resolves the article's enquiry and Book a meeting buttons once for every
 * surface on the page: the opening row and the hire section at its foot.
 */
export const ArticlePageCtasProvider = ({
    loaded,
    enquiryAudience,
    professional,
    enquiryProfessional,
    articleID,
    articleTitle,
    heroImage,
    children,
}: ArticlePageCtasProviderProps) => {
    const fallback = useMemo<PageCtasProviderProps['fallback']>(
        () => ({
            pageType: EnquiryFormPageType.Article,
            enquiryAudience: enquiryAudience ?? EnquiryAudienceEnum.Anybody,
            enquireLabel: ARTICLE_ENQUIRE_LABEL,
        }),
        [enquiryAudience],
    );
    const enquiry = useMemo<EnquiryModalContext>(
        () => ({
            professional: enquiryProfessional,
            itemType: 'article',
            siteTreeID: articleID,
            itemTitle: articleTitle,
            itemImage: heroImage,
            brandImage: heroImage,
        }),
        [enquiryProfessional, articleID, articleTitle, heroImage],
    );
    const book = useMemo<PageBookIntentBase>(
        () => ({
            professionalUrlSegment: professional.urlSegment ?? '',
            siteTreeId: articleID,
            professionalId: professional.id,
            itemTitle: articleTitle,
            professionalName: professional.title,
            companyLogo: professional.logo,
            logoBackgroundColor: professional.logoBackground,
            paneImage: heroImage,
        }),
        [
            professional.urlSegment,
            professional.id,
            professional.title,
            professional.logo,
            professional.logoBackground,
            articleID,
            articleTitle,
            heroImage,
        ],
    );

    return (
        <PageCtasProvider
            loaded={loaded}
            fallback={fallback}
            enquiry={enquiry}
            book={book}
        >
            {children}
            <CtaAutoOpen />
        </PageCtasProvider>
    );
};
